import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CURRENCIES, ITEM_TYPES, PURPOSE_TYPES } from "../constants";
import { api } from "../services/api";
import LegoImage from "./LegoImage";

function toInputDate(iso) {
  if (!iso || iso.startsWith("1970-01-01")) return "";
  return new Date(iso).toISOString().slice(0, 10);
}

function parseDateInput(input) {
  if (!input) return new Date(0).toISOString();
  const value = input.trim();
  const isoMatch = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const shortMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);

  let year;
  let month;
  let day;

  if (isoMatch) {
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  } else if (shortMatch) {
    month = Number(shortMatch[1]);
    day = Number(shortMatch[2]);
    year = Number(shortMatch[3]);
    if (year < 100) {
      year += 2000;
    }
  } else {
    return null;
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date.toISOString();
}

function toIsoDate(input) {
  return parseDateInput(input) || new Date(0).toISOString();
}

function EditPage({ isNew }) {
  const navigate = useNavigate();
  const { itemId } = useParams();
  const nextItemRowId = useRef(2);
  const rowLookupTimersRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saveText, setSaveText] = useState("Save");
  const [lastModified, setLastModified] = useState("");
  const [dateError, setDateError] = useState("");
  const [itemRows, setItemRows] = useState([
    {
      rowId: 1,
      displayName: "",
      autoFilledName: "",
      legoId: "",
      quantity: 1,
      price: "",
      priceCurrency: "USD",
      priceType: "unit_price",
      beforeTax: true,
    },
  ]);
  const [form, setForm] = useState({
    type: "buy",
    displayName: "",
    legoId: "",
    quantity: 1,
    purpose: "for_sale",
    price: "",
    priceType: "unit_price",
    priceCurrency: "USD",
    source: "",
    date: "",
    notes: "",
    fees: 6,
    beforeTax: true,
  });

  useEffect(() => {
    if (isNew || !itemId) return;
    setLoading(true);
    api
      .getItem(itemId)
      .then((item) => {
        setLastModified(item.lastModifiedDate || "");
        setForm({
          type: item.type === ITEM_TYPES.Buy ? "buy" : "sell",
          displayName: item.displayName || "",
          legoId: item.legoId || "",
          quantity: item.quantity ?? 1,
          purpose: item.purpose === PURPOSE_TYPES.ForSale ? "for_sale" : "for_play",
          price: item.unitPrice ?? 0,
          priceType: "unit_price",
          priceCurrency: CURRENCIES[item.currency] || "USD",
          source: item.source || "",
          date: toInputDate(item.date),
          notes: item.notes || "",
          fees: item.fees ?? 0,
          beforeTax: false,
        });
      })
      .catch((error) => window.alert(error.message))
      .finally(() => setLoading(false));
  }, [isNew, itemId]);

  useEffect(() => {
    return () => {
      Object.values(rowLookupTimersRef.current).forEach((timerId) =>
        clearTimeout(timerId)
      );
      rowLookupTimersRef.current = {};
    };
  }, []);

  const isValid = useMemo(() => {
    const hasValidDate = !form.date || Boolean(parseDateInput(form.date));
    if (isNew) {
      return (
        hasValidDate &&
        itemRows.length > 0 &&
        itemRows.every(
          (row) =>
            Boolean(row.legoId) &&
            Number(row.quantity) > 0 &&
            row.price !== ""
        )
      );
    }
    return Boolean(
      hasValidDate &&
        form.legoId &&
        Number(form.quantity) > 0 &&
        form.price !== ""
    );
  }, [isNew, form, itemRows]);

  function setField(field, value) {
    setSaveText("Save");
    if (field === "date") {
      setDateError("");
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function onDateBlur() {
    if (!form.date) {
      setDateError("");
      return;
    }

    const isoDate = parseDateInput(form.date);
    if (!isoDate) {
      setDateError("Use mm/dd/yy, mm/dd/yyyy, or yyyy-mm-dd.");
      return;
    }

    setDateError("");
    setForm((prev) => ({ ...prev, date: toInputDate(isoDate) }));
  }

  function onDatePaste(event) {
    const text = event.clipboardData.getData("text");
    const isoDate = parseDateInput(text);
    if (!isoDate) return;

    event.preventDefault();
    setDateError("");
    setField("date", toInputDate(isoDate));
  }

  async function resolveNameForRow(rowId, rawLegoId) {
    const legoId = String(rawLegoId || "").trim();
    if (!legoId) return;

    try {
      const result = await api.resolveLegoName(legoId);
      if (!result?.name) return;

      setItemRows((prev) =>
        prev.map((row) => {
          if (row.rowId !== rowId) return row;
          if (String(row.legoId || "").trim() !== legoId) return row;
          const hasManualName =
            row.displayName &&
            row.displayName.trim() !== "" &&
            row.displayName !== row.autoFilledName;
          if (hasManualName) return row;
          return {
            ...row,
            displayName: result.name,
            autoFilledName: result.name,
          };
        })
      );
    } catch (_error) {
      // Keep form usable if lookup fails (invalid legoId, missing API key, etc).
    }
  }

  function scheduleNameLookup(rowId, legoId) {
    if (!isNew) return;
    const existingTimer = rowLookupTimersRef.current[rowId];
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    rowLookupTimersRef.current[rowId] = setTimeout(() => {
      resolveNameForRow(rowId, legoId);
      delete rowLookupTimersRef.current[rowId];
    }, 350);
  }

  function setItemField(rowId, field, value) {
    setSaveText("Save");
    setItemRows((prev) =>
      prev.map((row) => {
        if (row.rowId !== rowId) return row;
        if (field === "displayName") {
          return { ...row, displayName: value, autoFilledName: "" };
        }
        return { ...row, [field]: value };
      })
    );
    if (field === "legoId") {
      scheduleNameLookup(rowId, value);
    }
  }

  function addItemRow() {
    setSaveText("Save");
    setItemRows((prev) => [
      ...prev,
      {
        rowId: nextItemRowId.current++,
        displayName: "",
        autoFilledName: "",
        legoId: "",
        quantity: 1,
        price: "",
        priceCurrency: "USD",
        priceType: "unit_price",
        beforeTax: true,
      },
    ]);
  }

  function removeItemRow(rowId) {
    setSaveText("Save");
    setItemRows((prev) => {
      if (prev.length <= 1) return prev;
      const existingTimer = rowLookupTimersRef.current[rowId];
      if (existingTimer) {
        clearTimeout(existingTimer);
        delete rowLookupTimersRef.current[rowId];
      }
      return prev.filter((row) => row.rowId !== rowId);
    });
  }

  function buildPayload(itemRow) {
    const type = form.type === "buy" ? ITEM_TYPES.Buy : ITEM_TYPES.Sell;
    const quantity = Number(itemRow?.quantity ?? form.quantity ?? 1);
    let unitPrice = Number(itemRow?.price ?? form.price ?? 0);
    const priceType = itemRow?.priceType ?? form.priceType;
    const priceCurrency = itemRow?.priceCurrency ?? form.priceCurrency;
    const beforeTax = itemRow?.beforeTax ?? form.beforeTax;

    if (priceType === "total_price") {
      unitPrice /= quantity;
    }
    if (type === ITEM_TYPES.Buy && beforeTax) {
      unitPrice *= 1.1;
    }

    return {
      id: itemId,
      type,
      displayName: itemRow?.displayName ?? form.displayName,
      legoId: itemRow?.legoId ?? form.legoId,
      quantity,
      purpose: form.purpose === "for_sale" ? PURPOSE_TYPES.ForSale : PURPOSE_TYPES.ForPlay,
      unitPrice: Math.round(unitPrice * 100) / 100,
      currency: CURRENCIES.indexOf(priceCurrency),
      source: form.source,
      date: toIsoDate(form.date),
      lastModifiedDate: new Date().toISOString(),
      notes: form.notes,
      fees: Number(form.fees || 0),
    };
  }

  async function onSubmit(event) {
    event.preventDefault();
    setSubmitted(true);
    if (!isValid) return;

    setLoading(true);
    try {
      if (isNew) {
        await Promise.all(itemRows.map((row) => api.createItem(buildPayload(row))));
        navigate("/");
      } else {
        const payload = buildPayload();
        await api.updateItem(itemId, payload);
        setSaveText("Saved!");
        setLastModified(new Date().toISOString());
      }
    } catch (error) {
      window.alert(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function onDelete() {
    if (!itemId) return;
    const ok = window.confirm("Are you sure to delete? Click OK to confirm.");
    if (!ok) return;

    setLoading(true);
    try {
      await api.deleteItem(itemId);
      navigate("/");
    } catch (error) {
      window.alert(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="edit-form" onSubmit={onSubmit}>
      <div className="row">
        <label>Record Type</label>
        <select value={form.type} onChange={(e) => setField("type", e.target.value)}>
          <option value="buy">Buy record</option>
          <option value="sell">Sell record</option>
        </select>
      </div>

      {isNew ? (
        <div className="row">
          <label>Items</label>
          <div className="table-wrap">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Lego ID</th>
                  <th>Name</th>
                  <th>Image</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {itemRows.map((row) => (
                  <tr key={row.rowId}>
                    <td>
                      <input
                        required
                        value={row.legoId}
                        onChange={(e) => setItemField(row.rowId, "legoId", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        value={row.displayName}
                        onChange={(e) => setItemField(row.rowId, "displayName", e.target.value)}
                      />
                    </td>
                    <td>
                      <LegoImage legoId={row.legoId} />
                    </td>
                    <td>
                      <input
                        required
                        type="number"
                        min="1"
                        className="qty-input"
                        value={row.quantity}
                        onChange={(e) => setItemField(row.rowId, "quantity", e.target.value)}
                      />
                    </td>
                    <td>
                      <div className="price-cell-row">
                        <select
                          value={row.priceCurrency}
                          onChange={(e) => setItemField(row.rowId, "priceCurrency", e.target.value)}
                        >
                          <option value="USD">$</option>
                          <option value="CNY">¥</option>
                          <option value="EUR">EUR</option>
                        </select>
                        <input
                          required
                          className="price-input"
                          type="number"
                          step="0.01"
                          value={row.price}
                          onChange={(e) => setItemField(row.rowId, "price", e.target.value)}
                        />
                        <select
                          value={row.priceType}
                          onChange={(e) => setItemField(row.rowId, "priceType", e.target.value)}
                        >
                          <option value="unit_price">unit</option>
                          <option value="total_price">total</option>
                        </select>
                        {form.type === "buy" && (
                          <label className="checkbox-inline">
                            <input
                              type="checkbox"
                              checked={Boolean(row.beforeTax)}
                              onChange={(e) => setItemField(row.rowId, "beforeTax", e.target.checked)}
                            />
                            Before Tax
                          </label>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => removeItemRow(row.rowId)}
                        disabled={itemRows.length <= 1 || loading}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: "10px" }}>
            <button type="button" onClick={addItemRow} disabled={loading}>
              Add item
            </button>
          </div>
        </div>
      ) : (
        <div className="grid-3">
          <div>
            <label>Name</label>
            <input value={form.displayName} onChange={(e) => setField("displayName", e.target.value)} />
          </div>
          <div>
            <label>Lego ID</label>
            <input required value={form.legoId} onChange={(e) => setField("legoId", e.target.value)} />
          </div>
          <div>
            <label>Quantity</label>
            <input
              required
              type="number"
              min="1"
              value={form.quantity}
              onChange={(e) => setField("quantity", e.target.value)}
            />
          </div>
        </div>
      )}

      {isNew ? (
        <div className="grid-3">
          <div>
            <label>{form.type === "buy" ? "Purchase Date" : "Sell Date"}</label>
            <input
              type="date"
              value={form.date}
              onBlur={onDateBlur}
              onPaste={onDatePaste}
              onChange={(e) => setField("date", e.target.value)}
            />
            {dateError && <div className="field-error">{dateError}</div>}
          </div>
          <div>
            <label>{form.type === "buy" ? "Purchase From" : "Sell Platform"}</label>
            <input value={form.source} onChange={(e) => setField("source", e.target.value)} />
          </div>
          {form.type === "buy" && (
            <div>
              <label>Purpose</label>
              <select
                value={form.purpose}
                onChange={(e) => setField("purpose", e.target.value)}
              >
                <option value="for_sale">For resale</option>
                <option value="for_play">For play</option>
              </select>
            </div>
          )}
          {form.type === "sell" && (
            <div>
              <label>Fees %</label>
              <input
                type="number"
                step="0.1"
                value={form.fees}
                onChange={(e) => setField("fees", e.target.value)}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="grid-3">
            <div>
              <label>{form.type === "buy" ? "Purchase Date" : "Sell Date"}</label>
              <input
                type="date"
                value={form.date}
                onBlur={onDateBlur}
                onPaste={onDatePaste}
                onChange={(e) => setField("date", e.target.value)}
              />
              {dateError && <div className="field-error">{dateError}</div>}
            </div>
            <div>
              <label>Price Settings</label>
              <div className="inline-group">
                <select value={form.priceCurrency} onChange={(e) => setField("priceCurrency", e.target.value)}>
                  <option value="USD">$</option>
                  <option value="CNY">¥</option>
                  <option value="EUR">EUR</option>
                </select>
                <input
                  required
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setField("price", e.target.value)}
                />
                <select value={form.priceType} onChange={(e) => setField("priceType", e.target.value)}>
                  <option value="unit_price">unit</option>
                  <option value="total_price">total</option>
                </select>
              </div>
              {form.type === "buy" && (
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={form.beforeTax}
                    onChange={(e) => setField("beforeTax", e.target.checked)}
                  />
                  Before Tax
                </label>
              )}
            </div>
            {form.type !== "buy" && (
              <div>
                <label>Fees %</label>
                <input type="number" step="0.1" value={form.fees} onChange={(e) => setField("fees", e.target.value)} />
              </div>
            )}
          </div>
          <div className="grid-2">
            <div>
              <label>{form.type === "buy" ? "Purchase From" : "Sell Platform"}</label>
              <input value={form.source} onChange={(e) => setField("source", e.target.value)} />
            </div>
            {form.type === "buy" && (
              <div>
                <label>Purpose</label>
                <select value={form.purpose} onChange={(e) => setField("purpose", e.target.value)}>
                  <option value="for_sale">For resale</option>
                  <option value="for_play">For play</option>
                </select>
              </div>
            )}
          </div>
        </>
      )}

      <div>
        <label>Extra notes</label>
        <textarea rows="4" value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
      </div>

      {submitted && !isValid && <div className="alert">Please fix form errors and try again.</div>}

      <div className="form-actions">
        <button type="submit" disabled={!isValid || loading}>
          {saveText}
        </button>
        {!isNew && (
          <button type="button" className="danger" onClick={onDelete} disabled={loading}>
            Delete
          </button>
        )}
      </div>

      {!isNew && lastModified && (
        <div className="last-updated">(Last update: {new Date(lastModified).toLocaleString()})</div>
      )}
    </form>
  );
}

export default EditPage;
